import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { LinkedinIcon } from '@/components/icons/linkedin'
import { urlFor } from '@/sanity/image'
import { authorInitial } from '@/features/blog/author-initial'

export interface AuthorCardData {
  name: string
  photo?: object
  role?: string
  bio?: string
  linkedin?: string
}

// Banner-style author card shown at the foot of an article (after the FAQ).
// Renders nothing without an author; each sub-block appears only when filled.
export function AuthorCard({
  author,
  label,
}: {
  author?: AuthorCardData
  label: string
}) {
  if (!author) return null
  return (
    <section className="mt-10 border-t border-border pt-10">
      <div className="flex flex-wrap items-center gap-4 rounded-xl border border-border bg-card p-5 shadow-sm">
        <Avatar className="size-14 flex-none rounded-full border-2 border-muted">
          {author.photo && (
            <AvatarImage
              src={urlFor(author.photo)
                .width(112)
                .height(112)
                .fit('crop')
                .url()}
              alt={author.name}
              className="object-cover"
            />
          )}
          <AvatarFallback className="bg-secondary font-serif text-xl font-bold text-primary">
            {authorInitial(author.name)}
          </AvatarFallback>
        </Avatar>

        <div className="min-w-0">
          <span className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            {label}
          </span>
          <p className="font-serif text-base font-bold text-foreground">
            {author.name}
            {author.role && (
              <span className="ml-2 text-sm font-semibold text-primary-vivid">
                {author.role}
              </span>
            )}
          </p>
          {author.bio && (
            <p className="mt-1 text-sm text-muted-foreground">{author.bio}</p>
          )}
        </div>

        {author.linkedin && (
          <a
            href={author.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LinkedIn"
            className="ml-auto flex size-9 flex-none items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:text-primary-vivid"
          >
            <LinkedinIcon className="size-4" />
          </a>
        )}
      </div>
    </section>
  )
}
